import { TV_CHART_BRIDGE_ACTION } from "./actions";

const CONFIRM_MESSAGES = {
  overwriteTemplate: {
    title: "Are you sure?",
    message:
      "Are you sure you want to overwrite this template with current chart settings?",
    confirmLabel: "Overwrite template"
  },
  applyTemplate: {
    title: "Are you sure?",
    message: "Are you sure you want to apply template settings to this chart?",
    confirmLabel: "Apply template"
  },
  restoreBackup: {
    title: "Are you sure?",
    message:
      "Are you sure you want restore this chart backup? This will replace all your settings and drawn objects."
  },
  applyAnalysis: {
    title: "Are you sure?",
    message: "Are you sure you want to apply analysis settings to this chart?"
  }
};

function isCancelledError(error) {
  return (
    error?.cancelled === true ||
    error?.payload?.cancelled === true ||
    (error && typeof error === "object" && error.cancelled)
  );
}

/**
 * Host-side RPC for template/backup/analysis chart actions inside tvchartbridge.html.
 */
export default function createChartActionsBridge({
  postMessage,
  getTemplatesList,
  getAnalysesList,
  getBackupsList,
  addChartTemplate,
  updateChartTemplate,
  removeChartTemplate,
  fetchChartTemplateDetailsById,
  addChartAnalysis,
  updateChartAnalysisMeta,
  setChartAnalysisVisibility,
  removeChartAnalysis,
  fetchAnalysisDetailsById,
  fetchBackupDetails,
  confirm
}) {
  const send = payload => {
    postMessage(JSON.stringify(payload));
  };

  const respond = (requestId, result, error = null) => {
    send({
      action: TV_CHART_BRIDGE_ACTION.DF_RESPONSE,
      requestId,
      data: error
        ? { ok: false, error }
        : { ok: true, result }
    });
  };

  const confirmAction = async confirmKey => {
    if (typeof confirm !== "function") {
      return true;
    }

    const config = CONFIRM_MESSAGES[confirmKey];
    if (!config) {
      return true;
    }

    const decision = await confirm({
      title: config.title,
      message: config.message,
      confirmLabel: config.confirmLabel
    });

    return decision === "confirm" || decision === true;
  };

  const handleMessage = message => {
    if (!message || !message.action) {
      return false;
    }

    const { action, requestId, data = {} } = message;

    switch (action) {
      case TV_CHART_BRIDGE_ACTION.TEMPLATES_LIST: {
        Promise.resolve()
          .then(() => getTemplatesList?.() || { list: [], defaultTemplate: null })
          .then(result => respond(requestId, result))
          .catch(error =>
            respond(requestId, null, error?.message || "TEMPLATES_LIST failed")
          );
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.ANALYSES_LIST: {
        Promise.resolve()
          .then(() => getAnalysesList?.() || { list: [] })
          .then(result => respond(requestId, result))
          .catch(error =>
            respond(requestId, null, error?.message || "ANALYSES_LIST failed")
          );
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.BACKUPS_LIST: {
        Promise.resolve()
          .then(() => getBackupsList?.() || [])
          .then(result => respond(requestId, { backups: result }))
          .catch(error =>
            respond(requestId, null, error?.message || "BACKUPS_LIST failed")
          );
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.TEMPLATE_ADD: {
        Promise.resolve()
          .then(() =>
            addChartTemplate?.({
              name: data.name,
              settings: data.settings
            })
          )
          .then(async () => {
            const listState = (await getTemplatesList?.()) || {
              list: [],
              defaultTemplate: null
            };
            respond(requestId, listState);
          })
          .catch(error =>
            respond(
              requestId,
              null,
              error?.message ||
                error?.validation?.[0]?.value ||
                "TEMPLATE_ADD failed"
            )
          );
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.ANALYSIS_ADD: {
        Promise.resolve()
          .then(() =>
            addChartAnalysis?.({
              name: data.name,
              description: data.description,
              settings: data.settings,
              ...(typeof data.is_public === "boolean"
                ? { is_public: data.is_public }
                : {})
            })
          )
          .then(async () => {
            const listState = (await getAnalysesList?.()) || { list: [] };
            respond(requestId, listState);
          })
          .catch(error =>
            respond(
              requestId,
              null,
              error?.message ||
                error?.validation?.[0]?.value ||
                "ANALYSIS_ADD failed"
            )
          );
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.TEMPLATE_OVERWRITE: {
        Promise.resolve()
          .then(async () => {
            const confirmed = await confirmAction("overwriteTemplate");
            if (!confirmed) {
              return { cancelled: true };
            }

            await updateChartTemplate?.({
              id: data.id,
              settings: data.settings
            });

            return getTemplatesList?.();
          })
          .then(result => respond(requestId, result || { cancelled: true }))
          .catch(error => {
            if (isCancelledError(error)) {
              respond(requestId, { cancelled: true });
              return;
            }
            respond(
              requestId,
              null,
              error?.message || "TEMPLATE_OVERWRITE failed"
            );
          });
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.TEMPLATE_RENAME_OR_UPDATE: {
        Promise.resolve()
          .then(() =>
            updateChartTemplate?.({
              id: data.id,
              ...(typeof data.name === "string" ? { name: data.name } : {}),
              ...(typeof data.is_default === "boolean"
                ? { is_default: data.is_default }
                : {})
            })
          )
          .then(async () => {
            const listState = (await getTemplatesList?.()) || {
              list: [],
              defaultTemplate: null
            };
            respond(requestId, listState);
          })
          .catch(error =>
            respond(
              requestId,
              null,
              error?.message || "TEMPLATE_RENAME_OR_UPDATE failed"
            )
          );
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.ANALYSIS_UPDATE_META: {
        Promise.resolve()
          .then(() =>
            updateChartAnalysisMeta?.({
              id: data.id,
              name: data.name,
              description: data.description
            })
          )
          .then(async () => {
            const listState = (await getAnalysesList?.()) || { list: [] };
            respond(requestId, listState);
          })
          .catch(error =>
            respond(
              requestId,
              null,
              error?.message || "ANALYSIS_UPDATE_META failed"
            )
          );
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.ANALYSIS_SET_VISIBILITY: {
        Promise.resolve()
          .then(async () => {
            if (typeof confirm === "function") {
              const decision = await confirm({
                title: data.confirmTitle || "Are you sure?",
                message: data.confirmMessage
              });
              if (!(decision === "confirm" || decision === true)) {
                return { cancelled: true };
              }
            }

            await setChartAnalysisVisibility?.({
              id: data.id,
              isPublic: data.isPublic
            });

            return getAnalysesList?.();
          })
          .then(result => respond(requestId, result || { cancelled: true }))
          .catch(error => {
            if (isCancelledError(error)) {
              respond(requestId, { cancelled: true });
              return;
            }
            respond(
              requestId,
              null,
              error?.message || "ANALYSIS_SET_VISIBILITY failed"
            );
          });
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.TEMPLATE_REMOVE: {
        Promise.resolve()
          .then(() => removeChartTemplate?.({ id: data.id }))
          .then(async () => {
            const listState = (await getTemplatesList?.()) || {
              list: [],
              defaultTemplate: null
            };
            respond(requestId, listState);
          })
          .catch(error => {
            if (isCancelledError(error)) {
              respond(requestId, { cancelled: true });
              return;
            }
            respond(
              requestId,
              null,
              error?.message || "TEMPLATE_REMOVE failed"
            );
          });
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.ANALYSIS_REMOVE: {
        Promise.resolve()
          .then(async () => {
            if (typeof confirm === "function") {
              const decision = await confirm({
                title: "Are you sure?",
                message: `Do you really want to delete ${data.name ||
                  "this analysis"}`
              });
              if (decision !== "confirm" && decision !== true) {
                return { cancelled: true };
              }
            }

            await removeChartAnalysis?.({ id: data.id });
            return getAnalysesList?.();
          })
          .then(result => respond(requestId, result || { cancelled: true }))
          .catch(error => {
            if (isCancelledError(error)) {
              respond(requestId, { cancelled: true });
              return;
            }
            respond(
              requestId,
              null,
              error?.message || "ANALYSIS_REMOVE failed"
            );
          });
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.TEMPLATE_APPLY_DETAILS: {
        Promise.resolve()
          .then(async () => {
            const confirmed = await confirmAction("applyTemplate");
            if (!confirmed) {
              return { cancelled: true };
            }

            return fetchChartTemplateDetailsById?.(data.id);
          })
          .then(result => respond(requestId, result || { cancelled: true }))
          .catch(error =>
            respond(
              requestId,
              null,
              error?.message || "TEMPLATE_APPLY_DETAILS failed"
            )
          );
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.ANALYSIS_APPLY_DETAILS: {
        Promise.resolve()
          .then(async () => {
            if (typeof confirm !== "function") {
              const details = await fetchAnalysisDetailsById?.(data.id);
              return details;
            }

            const config = CONFIRM_MESSAGES.applyAnalysis;
            const decision = await confirm({
              title: config.title,
              message: config.message,
              secondaryConfirmLabel: data.withSecondarySave
                ? "Save current analysis and use it"
                : undefined,
              decisions: data.withSecondarySave
                ? ["confirm", "cancel", "secondaryConfirm"]
                : ["confirm", "cancel"]
            });

            if (decision !== "confirm" && decision !== "secondaryConfirm") {
              return { cancelled: true };
            }

            const details = await fetchAnalysisDetailsById?.(data.id);
            return {
              ...details,
              saveCurrentFirst: decision === "secondaryConfirm"
            };
          })
          .then(result => respond(requestId, result || { cancelled: true }))
          .catch(error =>
            respond(
              requestId,
              null,
              error?.message || "ANALYSIS_APPLY_DETAILS failed"
            )
          );
        return true;
      }

      case TV_CHART_BRIDGE_ACTION.BACKUP_RESTORE_DETAILS: {
        Promise.resolve()
          .then(async () => {
            const confirmed = await confirmAction("restoreBackup");
            if (!confirmed) {
              return { cancelled: true };
            }

            return fetchBackupDetails?.(data.url);
          })
          .then(result => respond(requestId, result || { cancelled: true }))
          .catch(error =>
            respond(
              requestId,
              null,
              error?.message || "BACKUP_RESTORE_DETAILS failed"
            )
          );
        return true;
      }

      default:
        return false;
    }
  };

  const pushBackupsUpdated = backups => {
    send({
      action: TV_CHART_BRIDGE_ACTION.BACKUPS_UPDATED,
      data: { backups: backups || [] }
    });
  };

  const pushMarkersUpdate = markers => {
    send({
      action: TV_CHART_BRIDGE_ACTION.MARKERS_UPDATE,
      data: { markers: markers || {} }
    });
  };

  const pushDrawnObjectsOptionsUpdate = drawnObjectsOptions => {
    send({
      action: TV_CHART_BRIDGE_ACTION.DRAWN_OBJECTS_OPTIONS_UPDATE,
      data: { drawnObjectsOptions: drawnObjectsOptions || [] }
    });
  };

  const pushPriceAlertsUpdate = ({ priceAlerts, currency } = {}) => {
    send({
      action: TV_CHART_BRIDGE_ACTION.PRICE_ALERTS_UPDATE,
      data: {
        priceAlerts: priceAlerts || [],
        currency
      }
    });
  };

  const destroy = () => {};

  return {
    handleMessage,
    pushBackupsUpdated,
    pushMarkersUpdate,
    pushDrawnObjectsOptionsUpdate,
    pushPriceAlertsUpdate,
    destroy
  };
}
